import { AlertCard, Tag, Tooltip } from "@caprikorn/dynamo-ui";
import type { FC } from "react";
import { useParams } from "react-router-dom";

import type { UrlParam } from "@router/router.types";
import { useGetChildEntityBillingAccountAuxiliaryBalance } from "@services/apiEcms/useGetChildEntityBillingAccountAuxiliaryBalance";
import { useGetChildEntityBillingAccountBalance } from "@services/apiEcms/useGetChildEntityBillingAccountBalance";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./AccountBalance.module.css";

export const AccountBalance: FC = () => {
  const { t } = useCustomTranslation("main");
  const { customerId = "", contractId = "" } = useParams<UrlParam>();
  const {
    data: auxiliaryBalance,
    isError: isAuxiliaryBalanceError,
    isPending: isAuxiliaryBalancePending,
  } = useGetChildEntityBillingAccountAuxiliaryBalance({
    customerId,
    contractId,
  });
  const {
    data: accountBalance,
    isError: isAccountBalanceError,
    isLoading: isAccountBalanceLoading,
  } = useGetChildEntityBillingAccountBalance(
    {
      customerId,
      contractId,
    },
    Boolean(auxiliaryBalance && !auxiliaryBalance?.isLetteringError),
  );

  if (isAuxiliaryBalancePending || isAccountBalanceLoading) return null;

  if (isAuxiliaryBalanceError || isAccountBalanceError) {
    return (
      <AlertCard size="sm" variant="error">
        <AlertCard.Description>
          {t("perimeter.sites.invoiceAndPaymentsDetails.balanceError")}
        </AlertCard.Description>
      </AlertCard>
    );
  }

  if (auxiliaryBalance?.isLetteringError) {
    return (
      <AlertCard size="sm">
        <AlertCard.Description>
          <Tooltip hasArrow placement="top">
            <Tooltip.Trigger>
              {t(
                "perimeter.sites.invoiceAndPaymentsDetails.balanceUnavailableError.title",
              )}
            </Tooltip.Trigger>
            <Tooltip.Content>
              {t(
                "perimeter.sites.invoiceAndPaymentsDetails.balanceUnavailableError.description",
              )}
            </Tooltip.Content>
          </Tooltip>
        </AlertCard.Description>
      </AlertCard>
    );
  }

  return (
    <div className={classes.balanceTags}>
      <Tag
        color={accountBalance?.outstandingBalance === 0 ? "success" : "error"}
        label={t(
          "perimeter.sites.invoiceAndPaymentsDetails.outstandingBalance",
          {
            amount: accountBalance?.outstandingBalance,
            currency: "EUR",
          },
        )}
      />

      <Tag
        color={accountBalance?.unmaturedBalance === 0 ? "success" : "contrast"}
        label={t("perimeter.sites.invoiceAndPaymentsDetails.unmaturedBalance", {
          amount: accountBalance?.unmaturedBalance,
          currency: "EUR",
        })}
      />
    </div>
  );
};
